import { type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { createAppTheme } from "../theme/theme";
import { getStoredThemeMode } from "../theme/ThemeModeProvider";

// Imperative confirm, replacing antd `Modal.confirm(...)`. Returns a promise that
// resolves true on confirm / false on cancel or backdrop dismiss. Mounts its own
// root (so it works from anywhere, like antd's global) and wraps it in the app
// theme for the current mode — otherwise an out-of-tree render loses theming.

export interface ConfirmOptions {
  title?: ReactNode;
  content?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** Destructive action — red confirm button (antd okType="danger"). */
  danger?: boolean;
}

const ConfirmDialog = ({
  opts,
  onConfirm,
  onCancel,
}: {
  opts: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <Dialog open onClose={onCancel} aria-labelledby="confirm-dialog-title">
    {opts.title ? <DialogTitle id="confirm-dialog-title">{opts.title}</DialogTitle> : null}
    {opts.content ? (
      <DialogContent>
        <DialogContentText>{opts.content}</DialogContentText>
      </DialogContent>
    ) : null}
    <DialogActions>
      <Button onClick={onCancel} color="inherit">
        {opts.cancelText ?? "Cancel"}
      </Button>
      <Button onClick={onConfirm} variant="contained" color={opts.danger ? "error" : "primary"} autoFocus>
        {opts.confirmText ?? "OK"}
      </Button>
    </DialogActions>
  </Dialog>
);

export const confirmDialog = (opts: ConfirmOptions = {}): Promise<boolean> =>
  new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const theme = createAppTheme(getStoredThemeMode());

    const settle = (result: boolean) => {
      // defer unmount out of the event handler to avoid React "unmount during render" warnings
      setTimeout(() => {
        root.unmount();
        host.remove();
      }, 0);
      resolve(result);
    };

    root.render(
      <ThemeProvider theme={theme}>
        <ConfirmDialog opts={opts} onConfirm={() => settle(true)} onCancel={() => settle(false)} />
      </ThemeProvider>,
    );
  });
